// @figma file=nq2oTuNpAeyLOoF0tXtO32 node=1-3 name="Button" set="shadcn-studio-pro-v5" verified=2026-03-16

import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { cn } from '../../lib/utils';

// ─── Base ─────────────────────────────────────────────────────────────────────

export const buttonBase =
  'inline-flex items-center justify-center whitespace-nowrap rounded-[var(--button-radius,var(--radius))] font-medium transition-colors cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 active:scale-[0.98] [&_svg]:pointer-events-none [&_svg]:shrink-0';

// ─── Variant config ───────────────────────────────────────────────────────────

export const buttonVariantConfig = {
  variants: {
    /**
     * Color intent — matches Figma `style` dimension.
     * Maps directly to semantic tokens: bg-{variant}, text-{variant}-foreground, etc.
     */
    variant: {
      primary: '',
      secondary: '',
      info: '',
      success: '',
      warning: '',
      destructive: '',
    },
    /**
     * Visual appearance — matches Figma button type dimension.
     * solid    → filled background (Figma: Solid Button)
     * soft     → semi-transparent tinted background (Figma: Soft Button)
     * outline  → border only, transparent background (Figma: Outline Button)
     * ghost    → no background or border, hover reveals bg (Figma: Ghost Button)
     * link     → text only with underline on hover
     */
    appearance: {
      solid: '',
      soft: '',
      outline: 'border bg-transparent',
      ghost: 'bg-transparent',
      link: 'underline-offset-4 hover:underline bg-transparent',
      gradient: 'text-white shadow-md hover:opacity-90 active:opacity-80',
    },
    /**
     * Size — matches Figma `size` dimension.
     * xs → h-7  (new — Figma xs ≈ 26px)
     * sm → h-8  (Figma sm = 32px)
     * default → h-9  (Figma md = 36px)
     * lg → h-10 (Figma lg = 40px)
     * icon → square h-9 × w-9
     */
    size: {
      xs: 'h-7 px-2.5 text-xs gap-1   [&_svg]:size-3',
      sm: 'h-8 px-3   text-sm gap-1.5 [&_svg]:size-3.5',
      default: 'h-9 px-4   text-sm gap-2   [&_svg]:size-4',
      lg: 'h-10 px-6  text-sm gap-2   [&_svg]:size-4',
      icon: 'h-9 w-9                    [&_svg]:size-4',
    },
    /**
     * Shape — matches Figma `shape` dimension.
     * default → uses --radius token (theme-controlled)
     * pill    → fully rounded (Figma: shape=round)
     */
    shape: {
      default: '',
      pill: 'rounded-full',
    },
  },

  compoundVariants: [
    // ── Solid ──────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'solid' as const,
      class: 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90',
    },
    {
      variant: 'secondary' as const,
      appearance: 'solid' as const,
      class: 'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
    },
    {
      variant: 'info' as const,
      appearance: 'solid' as const,
      class: 'bg-info text-info-foreground shadow-sm hover:bg-info/90',
    },
    {
      variant: 'success' as const,
      appearance: 'solid' as const,
      class: 'bg-success text-success-foreground shadow-sm hover:bg-success/90',
    },
    {
      variant: 'warning' as const,
      appearance: 'solid' as const,
      class: 'bg-warning text-warning-foreground shadow-sm hover:bg-warning/90',
    },
    {
      variant: 'destructive' as const,
      appearance: 'solid' as const,
      class: 'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
    },

    // ── Soft ───────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'soft' as const,
      class: 'bg-primary/10 text-primary hover:bg-primary/20',
    },
    {
      variant: 'secondary' as const,
      appearance: 'soft' as const,
      class: 'bg-secondary text-secondary-foreground hover:bg-secondary/70',
    },
    {
      variant: 'info' as const,
      appearance: 'soft' as const,
      class: 'bg-info/10 text-info hover:bg-info/20',
    },
    {
      variant: 'success' as const,
      appearance: 'soft' as const,
      class: 'bg-success/10 text-success hover:bg-success/20',
    },
    {
      variant: 'warning' as const,
      appearance: 'soft' as const,
      class: 'bg-warning/10 text-warning hover:bg-warning/20',
    },
    {
      variant: 'destructive' as const,
      appearance: 'soft' as const,
      class: 'bg-destructive/10 text-destructive hover:bg-destructive/20',
    },

    // ── Outline ────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'outline' as const,
      class: 'border-primary text-primary hover:bg-primary/10',
    },
    {
      variant: 'secondary' as const,
      appearance: 'outline' as const,
      class: 'border-input text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
    },
    {
      variant: 'info' as const,
      appearance: 'outline' as const,
      class: 'border-info text-info hover:bg-info/10',
    },
    {
      variant: 'success' as const,
      appearance: 'outline' as const,
      class: 'border-success text-success hover:bg-success/10',
    },
    {
      variant: 'warning' as const,
      appearance: 'outline' as const,
      class: 'border-warning text-warning hover:bg-warning/10',
    },
    {
      variant: 'destructive' as const,
      appearance: 'outline' as const,
      class: 'border-destructive text-destructive hover:bg-destructive/10',
    },

    // ── Ghost ──────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'ghost' as const,
      class: 'text-primary hover:bg-primary/10',
    },
    {
      variant: 'secondary' as const,
      appearance: 'ghost' as const,
      class: 'text-secondary-foreground hover:bg-accent hover:text-accent-foreground',
    },
    { variant: 'info' as const, appearance: 'ghost' as const, class: 'text-info hover:bg-info/10' },
    {
      variant: 'success' as const,
      appearance: 'ghost' as const,
      class: 'text-success hover:bg-success/10',
    },
    {
      variant: 'warning' as const,
      appearance: 'ghost' as const,
      class: 'text-warning hover:bg-warning/10',
    },
    {
      variant: 'destructive' as const,
      appearance: 'ghost' as const,
      class: 'text-destructive hover:bg-destructive/10',
    },

    // ── Link ───────────────────────────────────────────────────────────────────
    { variant: 'primary' as const, appearance: 'link' as const, class: 'text-primary' },
    {
      variant: 'secondary' as const,
      appearance: 'link' as const,
      class: 'text-secondary-foreground',
    },
    { variant: 'info' as const, appearance: 'link' as const, class: 'text-info' },
    { variant: 'success' as const, appearance: 'link' as const, class: 'text-success' },
    { variant: 'warning' as const, appearance: 'link' as const, class: 'text-warning' },
    { variant: 'destructive' as const, appearance: 'link' as const, class: 'text-destructive' },

    // ── Gradient ──────────────────────────────────────────────────────────────────
    {
      variant: 'primary' as const,
      appearance: 'gradient' as const,
      class: 'bg-gradient-to-r from-primary to-violet-600',
    },
    {
      variant: 'secondary' as const,
      appearance: 'gradient' as const,
      class: 'bg-gradient-to-r from-zinc-700 to-zinc-900 dark:from-zinc-600 dark:to-zinc-800',
    },
    {
      variant: 'info' as const,
      appearance: 'gradient' as const,
      class: 'bg-gradient-to-r from-info to-blue-600',
    },
    {
      variant: 'success' as const,
      appearance: 'gradient' as const,
      class: 'bg-gradient-to-r from-success to-emerald-600',
    },
    {
      variant: 'warning' as const,
      appearance: 'gradient' as const,
      class: 'bg-gradient-to-r from-warning to-orange-500',
    },
    {
      variant: 'destructive' as const,
      appearance: 'gradient' as const,
      class: 'bg-gradient-to-r from-destructive to-rose-700',
    },
  ],

  defaultVariants: {
    variant: 'primary' as const,
    appearance: 'solid' as const,
    size: 'default' as const,
    shape: 'default' as const,
  },
};

const buttonVariants = cva(buttonBase, buttonVariantConfig);

// ─── Props ────────────────────────────────────────────────────────────────────

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

// ─── Component ────────────────────────────────────────────────────────────────

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, appearance, size, shape, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, appearance, size, shape }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
